import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { memo } from 'react';

import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { TableCell } from '@flipster/ui/Table';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import { OrderMarginType } from '@flipster/utils/types/base';

import { getSymbolPathname } from '@web/app/[locale]/(web)/trade/_misc/pathname';

import { openPositionRowDataBunja } from '../row-bunja';

interface Props {
  index: number;
}

const MarginTypeLeverage = ({ leverage, marginType }: { leverage: string; marginType: OrderMarginType }) => {
  switch (marginType) {
    case 'Cross':
      return <FormattedMessage id='Trade.r251008.Cross_leverage' values={{ leverage: `${leverage}x` }} />;
    case 'Isolated':
      return <FormattedMessage id='Trade.r251008.Isolated_leverage' values={{ leverage: `${leverage}x` }} />;
    default:
      marginType satisfies never;
      return <>{leverage}x</>;
  }
};

const SymbolSideLeverageCell = memo(({ index }: Props) => {
  const router = useRouter();
  const { symbol, sideAtom, marginTypeAtom, leverageDisplayAtom } = useBunja(openPositionRowDataBunja);
  const side = useAtomValue(sideAtom);
  const marginType = useAtomValue(marginTypeAtom);
  const leverage = useAtomValue(leverageDisplayAtom);

  return (
    <TableCell>
      <button
        type='button'
        className={stack({ position: 'relative', pl: 16, gap: 5 })}
        onClick={() => {
          router.push(getSymbolPathname(symbol, false));
          segment.track(TradeEvent.TradeCryptoClick, {
            trade_type: 'perpetual',
            carousel_type: 'Your positions',
            symbol,
            list_order: index,
            search_type: null,
            search_word: null,
            price_change_percentage: null,
          });
        }}
      >
        <div
          className={css({
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            w: 4,
            h: 50,
            rounded: 2,
            bg: side === 'Long' ? 'content.accent.buy' : 'content.accent.sell',
          })}
        />
        <SymbolDisplay
          symbol={symbol}
          icon={<SymbolDisplay.Icon size={16} />}
          mainLabel={
            <SymbolDisplay.Label
              value='shortName'
              css={{ textStyle: 'Text/S/Strong', color: 'content.default.level-5' }}
            />
          }
        />
        <p className={css({ textStyle: 'Text/XS/Monospace/Regular', color: 'content.default.level-3' })}>
          <MarginTypeLeverage leverage={leverage} marginType={marginType} />
        </p>
      </button>
    </TableCell>
  );
});

export default SymbolSideLeverageCell;
